import { arrayValue, intValue, mapValue, stringValue } from "@/utils/coerce";
import { formatMs } from "@/utils/format";
import {
  promptBindingsOf,
  type PromptBinding,
  type TraceNode,
} from "@/utils/trace/model";
import { PromptBindingBadge } from "./PromptBindingBadge";
import { readExecutorSummary, type ExecutorSummary } from "./SubAgentSummary";

// A fetched page can run to megabytes; nothing in the panel is worth that.
const MAX_BLOCK_CHARS = 20000;

function clip(text: string) {
  return text.length > MAX_BLOCK_CHARS
    ? `${text.slice(0, MAX_BLOCK_CHARS)}\n… truncated`
    : text;
}

function prettyJson(value: unknown) {
  if (value === undefined || value === null) return "";
  try {
    const text = JSON.stringify(value, null, 2);
    if (!text || text === "{}" || text === "[]") return "";
    return clip(text);
  } catch {
    return String(value);
  }
}

/** A prose field. Non-strings are left to `prettyJson` rather than stringified. */
function longText(value: unknown) {
  if (typeof value !== "string") return "";
  const text = value.trim();
  return text ? clip(text) : "";
}

// Mirrors the DetailBlock in ToolCallGroups so tool output reads the same
// whether you reach it from the tool-call tree or from the timeline.
function DetailBlock({ title, children }: { title: string; children: string }) {
  return (
    <div className="border-t border-border pt-3 first:border-t-0 first:pt-0">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">{title}</div>
      <pre className="block max-w-full overflow-x-hidden rounded-lg border border-border bg-surface-input p-3 font-mono text-xs whitespace-pre-wrap text-code-foreground wrap-anywhere">
        {children}
      </pre>
    </div>
  );
}

function BulletBlock({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="border-t border-border pt-3 first:border-t-0 first:pt-0">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">{title}</div>
      <ul className="ml-4 list-disc text-sm">
        {items.map((item, index) => (
          // Index-keyed: two criteria can legitimately be the same string.
          <li key={`${index}-${item}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/** The sub-agent's report, as prose and lists rather than a JSON dump. */
function ExecutorReport({ summary }: { summary: ExecutorSummary }) {
  return (
    <>
      {summary.objective && <DetailBlock title="Objective">{summary.objective}</DetailBlock>}
      <BulletBlock title="Success criteria" items={summary.successCriteria} />
      <BulletBlock title="Preferred skills" items={summary.preferredSkills} />
      {summary.findings.length > 0 && (
        <div className="border-t border-border pt-3 first:border-t-0 first:pt-0">
          <div className="mb-1.5 text-xs font-medium text-muted-foreground">
            Findings{summary.status ? ` · ${summary.status}` : ""}
          </div>
          <ul className="ml-4 list-disc text-sm">
            {summary.findings.map((finding, index) => (
              <li key={`${index}-${finding.claim}`}>
                {finding.claim}
                {finding.evidenceCount > 0 && (
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    evidence {finding.evidenceCount}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <BulletBlock title="Uncertainties" items={summary.uncertainties} />
      <BulletBlock title="Recommended next actions" items={summary.recommendedNextActions} />
    </>
  );
}

/**
 * Which blocks went into this turn's prompt, and how big each was.
 *
 * Sizes only — the assembled text is deliberately not stored (migration 0193
 * scoped that out), so this answers "which blocks, and what did they cost"
 * rather than "what did they say". The version badges are what carry the
 * second half: a block's content is its prompt row at that version.
 *
 * `est_tokens` is a local cl100k_base count, not the tokenizer the provider
 * billed with, so the parts never sum to `prompt_token_count` exactly. The
 * difference is the caller's to show; this block does not pretend it away.
 */
function PromptComposition({
  detail,
  openBinding,
  onOpenBinding,
}: {
  detail: Record<string, unknown>;
  openBinding?: PromptBinding | null;
  onOpenBinding?: (binding: PromptBinding) => void;
}) {
  const params = mapValue(detail.request_params);
  const parts = arrayValue(params.prompt_parts)
    .map((raw) => {
      const part = mapValue(raw);
      return {
        name: stringValue(part.name, ""),
        chars: intValue(part.chars) ?? 0,
        tokens: intValue(part.est_tokens) ?? 0,
      };
    })
    .filter((part) => part.name)
    .sort((a, b) => b.tokens - a.tokens);

  // Shared with the token rows: the same badge must open the same thing in
  // both places, or pressing it teaches two different lessons.
  const bindings = promptBindingsOf(detail);

  const registered = detail.registered_tool_names;
  const toolNames = Array.isArray(registered) ? registered.map(String) : null;

  if (parts.length === 0 && bindings.length === 0 && toolNames === null) return null;

  return (
    <div className="border-t border-border pt-3 first:border-t-0 first:pt-0">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">
        프롬프트 구성
      </div>
      {bindings.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {bindings.map((binding) => (
            <PromptBindingBadge
              key={binding.id}
              binding={binding}
              open={openBinding?.id === binding.id}
              onOpen={onOpenBinding}
            />
          ))}
        </div>
      )}
      {parts.length > 0 ? (
        <ul className="grid gap-0.5 text-sm">
          {parts.map((part) => (
            <li key={part.name} className="flex items-baseline gap-2">
              <span className="font-mono text-xs">{part.name}</span>
              <span className="ml-auto tabular-nums text-xs text-muted-foreground">
                {part.chars.toLocaleString()} chars
              </span>
              <span className="w-24 text-right tabular-nums">
                ~{part.tokens.toLocaleString()} tok
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">
          이 턴에는 블록별 기록이 없습니다 — 프롬프트 파트를 남기기 시작하기 전에
          기록된 턴입니다.
        </p>
      )}
      {toolNames !== null && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {/* An empty list is a fact, not a gap: the turn ran with no tools
              registered, which the tool calls alone cannot express. */}
          등록된 도구 {toolNames.length}개
          {toolNames.length > 0 && ` · ${toolNames.join(", ")}`}
        </p>
      )}
    </div>
  );
}

/**
 * What a batch of simultaneous calls bought.
 *
 * The envelope has no payload of its own — its calls carry theirs — so the one
 * thing worth saying about it is the arithmetic: the wall clock against what
 * the same calls would have cost one after another.
 */
function BatchSummary({ detail }: { detail: Record<string, unknown> }) {
  const callCount = intValue(detail.call_count);
  const wallClockMs = intValue(detail.wall_clock_ms);
  const serialTotalMs = intValue(detail.serial_total_ms);
  const toolNames = arrayValue(detail.tool_names)
    .map((name) => stringValue(name, ""))
    .filter(Boolean);

  return (
    <div className="rounded-lg border border-border bg-surface-input p-2.5 text-xs">
      도구 {callCount ?? toolNames.length}개를 동시에 호출했습니다 — 실제{" "}
      <strong>{formatMs(wallClockMs)}</strong>, 순차 실행이었다면{" "}
      <strong>{formatMs(serialTotalMs)}</strong>.
      {toolNames.length > 0 && (
        <div className="mt-1 font-mono text-muted-foreground">
          {toolNames.join(", ")}
        </div>
      )}
      <div className="mt-1 text-muted-foreground">
        결과를 호출 순서대로 수집하므로 각 호출의 종료 시각은 상한값입니다.
      </div>
    </div>
  );
}

/**
 * Detail for one timeline node. Nothing here is serialized until a node is
 * actually selected — a long trace with several sub-agents runs to megabytes
 * and the endpoint has no size cap.
 */
export function TraceNodeDetail({
  node,
  openBinding,
  onOpenBinding,
}: {
  node: TraceNode;
  openBinding?: PromptBinding | null;
  onOpenBinding?: (binding: PromptBinding) => void;
}) {
  const raw = node.detail;
  const reasoning = longText(raw.reasoning);
  const responseText = longText(raw.response_text);
  const args = prettyJson(raw.arguments);
  // Reconstructed sub-agent tool calls come from a `raw_evidence` entry, which
  // names the tool's payload `data` rather than `result`.
  const result = prettyJson(raw.result ?? raw.data);
  // What the sub-agent actually read back — the closest thing a replay-only
  // trace has to a round transcript.
  const toolOutput = longText(raw.formatted_text) || longText(raw.read_preview);
  const metadata = prettyJson(raw.metadata);
  const roundIndex = intValue(raw.round_index);
  const usage = prettyJson(raw.usage ?? raw.usage_payload);
  // Three different error representations across the response: a string on main
  // tool calls, a string on sub-agent tool calls, an object on citation spans
  // and sub-agent executions.
  const error =
    stringValue(raw.error_message, "") ||
    (typeof raw.error === "string" ? raw.error : prettyJson(raw.error)) ||
    prettyJson(raw.error_payload);
  // Sub-agent execution payload, read by name rather than dumped as JSON —
  // findings and next actions are the whole point of a sub-agent's report.
  const executor = readExecutorSummary(raw);
  // What this sub-agent round tried to call — otherwise lost entirely.
  const functionCalls = prettyJson(raw.function_calls);
  // Which blocks were injected, and which prompt version each came from. Only
  // a main turn records these; a sub-agent round or a citation span does not.
  const composition = (
    <PromptComposition
      detail={raw}
      openBinding={openBinding}
      onOpenBinding={onOpenBinding}
    />
  );

  return (
    <div className="mt-3 grid gap-3 rounded-lg border border-border bg-surface-subtle p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <strong>{node.label}</strong>
        {node.rawStatus && (
          <span className="text-muted-foreground">{node.rawStatus}</span>
        )}
        {roundIndex !== null && (
          <span className="text-muted-foreground">round {roundIndex}</span>
        )}
        <span className="text-muted-foreground">{formatMs(node.durationMs)}</span>
        {node.model && (
          <span className="font-mono text-xs text-muted-foreground">
            {node.provider ? `${node.provider} / ` : ""}
            {node.model}
          </span>
        )}
        {node.startMs !== null && (
          <span className="text-xs text-muted-foreground">
            {formatMs(node.startMs)} → {formatMs(node.endMs)}
          </span>
        )}
      </div>

      {node.finalizeAnswer && (
        <div className="rounded-lg border border-border bg-surface-input p-2.5 text-xs">
          이 턴은 <code>finalize_answer</code>(citation 확정 배리어)만 호출했습니다. 도구 호출이
          누락된 것이 아닙니다.
        </div>
      )}

      {node.resultSlimmed && (
        <div className="rounded-lg border border-border bg-surface-input p-2.5 text-xs">
          이 결과는 슬림화되었습니다 — 서브에이전트 transcript는 아래 sub-agent 레인에 있습니다.
          <code className="ml-1">summary</code>만 남아 있습니다.
        </div>
      )}

      {node.kind === "tool_batch" && <BatchSummary detail={raw} />}
      {composition}
      {executor && <ExecutorReport summary={executor} />}
      {reasoning && <DetailBlock title="Reasoning (admin only)">{reasoning}</DetailBlock>}
      {responseText && <DetailBlock title="Response">{responseText}</DetailBlock>}
      {functionCalls && <DetailBlock title="Function calls">{functionCalls}</DetailBlock>}
      {args && <DetailBlock title="Arguments">{args}</DetailBlock>}
      {toolOutput && (
        <DetailBlock title="도구 출력 (모델이 본 내용)">{toolOutput}</DetailBlock>
      )}
      {result && <DetailBlock title="Result">{result}</DetailBlock>}
      {metadata && <DetailBlock title="Metadata">{metadata}</DetailBlock>}
      {error && <DetailBlock title="Error">{error}</DetailBlock>}
      {usage && <DetailBlock title="Usage">{usage}</DetailBlock>}
      {node.kind !== "tool_batch" &&
        composition === null &&
        !executor &&
        !reasoning &&
        !responseText &&
        !functionCalls &&
        !args &&
        !toolOutput &&
        !result &&
        !metadata &&
        !error &&
        !usage && (
          <p className="text-sm text-muted-foreground">
            이 노드에 저장된 상세 payload가 없습니다.
          </p>
        )}
    </div>
  );
}
